import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Settings, Key, Building2, Plug, RefreshCw, CheckCircle2, XCircle, 
  Loader2, ExternalLink, Trash2, Plus, Cpu, Wifi, Power, Thermometer,
  Tv, AirVent, Lightbulb, Zap, Activity
} from 'lucide-react';
import { toast } from 'sonner';

// IR Appliance type icons
const APPLIANCE_ICONS = {
  1: AirVent,      // Air Conditioner
  2: Tv,           // TV
  3: Cpu,          // Set Top Box
  4: Activity,     // Projector
  5: Cpu,          // Disc Player
  6: Cpu,          // AV Receiver
  7: Wifi          // Media Streamer
};

export default function KIOTIntegration() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [authStatus, setAuthStatus] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [hotelMappings, setHotelMappings] = useState([]);
  const [devices, setDevices] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [irTypes, setIrTypes] = useState([]);
  
  // Form states
  const [configForm, setConfigForm] = useState({
    client_id: '',
    client_secret: '',
    redirect_uri: '',
    scope: 'read'
  });
  const [saving, setSaving] = useState(false);
  
  // Dialogs
  const [addMappingOpen, setAddMappingOpen] = useState(false);
  const [syncDevicesOpen, setSyncDevicesOpen] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [syncEntityTypes, setSyncEntityTypes] = useState([1, 2, 3]);
  const [syncIrFilters, setSyncIrFilters] = useState([]);
  const [syncing, setSyncing] = useState(false);
  
  // Add Device dialog
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const [newDevice, setNewDevice] = useState({
    name: '',
    device_type: '',
    hotel_id: '',
    room_hint: '',
    ir_appliance_type: ''
  });
  const [addingDevice, setAddingDevice] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, authRes, companiesRes, mappingsRes, devicesRes, entityRes, irRes] = await Promise.all([
        api.get('/kiot/config'),
        api.get('/kiot/auth-status').catch(() => ({ data: { authenticated: false } })),
        api.get('/companies').catch(() => ({ data: [] })),
        api.get('/kiot/hotel-mappings').catch(() => ({ data: [] })),
        api.get('/kiot/devices').catch(() => ({ data: [] })),
        api.get('/kiot/entity-types'),
        api.get('/kiot/ir-appliance-types')
      ]);
      
      // Fetch hotels for each company
      const allHotels = [];
      if (Array.isArray(companiesRes.data)) {
        for (const company of companiesRes.data) {
          try {
            const hotelsRes = await api.get(`/companies/${company.id}/hotels`);
            if (Array.isArray(hotelsRes.data)) {
              hotelsRes.data.forEach(hotel => {
                allHotels.push({ ...hotel, company_name: company.name });
              });
            }
          } catch (e) {
            console.error(`Failed to fetch hotels for company ${company.name}:`, e);
          }
        }
      }
      
      setConfig(configRes.data);
      setAuthStatus(authRes.data);
      setHotels(allHotels);
      setHotelMappings(mappingsRes.data || []);
      setDevices(devicesRes.data || []);
      setEntityTypes(entityRes.data || []);
      setIrTypes(irRes.data || []);
      
      if (configRes.data.configured) {
        setConfigForm({
          client_id: configRes.data.client_id || '',
          client_secret: '',
          redirect_uri: configRes.data.redirect_uri || '',
          scope: configRes.data.scope || 'read'
        });
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load KIOT configuration'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!configForm.client_id || !configForm.client_secret || !configForm.redirect_uri) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/kiot/config', configForm);
      toast.success('KIOT configuration saved');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save configuration'));
    } finally {
      setSaving(false);
    }
  };

  const handleAuthenticate = async () => {
    try {
      const res = await api.get('/kiot/auth-url');
      // Open KIOT auth in new window
      window.open(res.data.auth_url, 'KIOT Auth', 'width=600,height=700');
      toast.info('Complete authentication in the popup window');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to get auth URL'));
    }
  };

  const handleRefreshToken = async () => {
    try {
      await api.post('/kiot/refresh-token');
      toast.success('Token refreshed successfully');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to refresh token'));
    }
  };

  const handleAddMapping = async () => {
    if (!selectedHotel) {
      toast.error('Please select a hotel');
      return;
    }
    
    try {
      await api.post('/kiot/hotel-mappings', {
        hotel_id: selectedHotel,
        is_enabled: true
      });
      toast.success('Hotel mapping added');
      setAddMappingOpen(false);
      setSelectedHotel('');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add mapping'));
    }
  };

  const handleDeleteMapping = async (hotelId) => {
    if (!confirm('Are you sure you want to remove this hotel mapping?')) return;
    
    try {
      await api.delete(`/kiot/hotel-mappings/${hotelId}`);
      toast.success('Mapping removed');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove mapping'));
    }
  };

  const handleAddDevice = async () => {
    if (!newDevice.name || !newDevice.device_type || !newDevice.hotel_id) {
      toast.error('Please fill in Device Name, Type, and select a Hotel');
      return;
    }
    
    setAddingDevice(true);
    try {
      await api.post('/kiot/devices', {
        name: newDevice.name,
        device_type: parseInt(newDevice.device_type),
        hotel_id: newDevice.hotel_id,
        room_hint: newDevice.room_hint || null,
        ir_appliance_type: newDevice.ir_appliance_type ? parseInt(newDevice.ir_appliance_type) : null
      });
      toast.success('Device added successfully');
      setAddDeviceOpen(false);
      setNewDevice({ name: '', device_type: '', hotel_id: '', room_hint: '', ir_appliance_type: '' });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add device'));
    } finally {
      setAddingDevice(false);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!confirm('Are you sure you want to delete this device?')) return;
    
    try {
      await api.delete(`/kiot/devices/${deviceId}`);
      toast.success('Device deleted');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete device'));
    }
  };

  const handleSyncDevices = async () => {
    if (!selectedHotel) {
      toast.error('Please select a hotel');
      return;
    }
    
    setSyncing(true);
    try {
      const res = await api.post('/kiot/sync-devices', {
        hotel_id: selectedHotel,
        entity_types: syncEntityTypes,
        ir_filters: syncIrFilters.length > 0 ? syncIrFilters : undefined
      });
      
      const deviceCount = res.data?.payload?.devices?.length || 0;
      toast.success(`Synced ${deviceCount} devices from KIOT`);
      setSyncDevicesOpen(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sync devices'));
    } finally {
      setSyncing(false);
    }
  };

  const toggleEntityType = (typeCode) => {
    setSyncEntityTypes(prev => 
      prev.includes(typeCode) 
        ? prev.filter(t => t !== typeCode)
        : [...prev, typeCode]
    );
  };

  const toggleIrFilter = (typeCode) => {
    setSyncIrFilters(prev => 
      prev.includes(typeCode) 
        ? prev.filter(t => t !== typeCode)
        : [...prev, typeCode]
    );
  };

  // Get unmapped hotels
  const unmappedHotels = hotels.filter(h => !hotelMappings.find(m => m.hotel_id === h.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KIOT Device Integration</h1>
          <p className="text-gray-500 mt-1">Configure and manage third-party IoT device integration</p>
        </div>
        <Badge variant={authStatus?.authenticated ? "default" : "destructive"} className="text-sm py-1 px-3">
          {authStatus?.authenticated ? (
            <><CheckCircle2 className="h-4 w-4 mr-1" /> Connected</>
          ) : (
            <><XCircle className="h-4 w-4 mr-1" /> Not Connected</>
          )}
        </Badge>
      </div>

      <Tabs defaultValue="config" className="space-y-6">
        <TabsList className="bg-gray-100">
          <TabsTrigger value="config" className="data-[state=active]:bg-white">
            <Settings className="h-4 w-4 mr-2" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="hotels" className="data-[state=active]:bg-white">
            <Building2 className="h-4 w-4 mr-2" />
            Hotel Mappings
          </TabsTrigger>
          <TabsTrigger value="devices" className="data-[state=active]:bg-white">
            <Cpu className="h-4 w-4 mr-2" />
            Devices
          </TabsTrigger>
        </TabsList>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* OAuth Credentials */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-orange-500" />
                  OAuth Credentials
                </CardTitle>
                <CardDescription>
                  Enter your KIOT API credentials to enable integration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Client ID *</Label>
                  <Input
                    value={configForm.client_id}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, client_id: e.target.value }))}
                    placeholder="Your KIOT Client ID"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Secret *</Label>
                  <Input
                    type="password"
                    value={configForm.client_secret}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, client_secret: e.target.value }))}
                    placeholder={config?.configured ? "••••••••" : "Your KIOT Client Secret"}
                  />
                  {config?.configured && (
                    <p className="text-xs text-gray-500">Leave blank to keep existing secret</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Redirect URI *</Label>
                  <Input
                    value={configForm.redirect_uri}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, redirect_uri: e.target.value }))}
                    placeholder="https://your-app.com/kiot/callback"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scope</Label>
                  <Input
                    value={configForm.scope}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, scope: e.target.value }))}
                    placeholder="read"
                  />
                </div>
                <Button 
                  onClick={handleSaveConfig} 
                  disabled={saving}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Save Configuration
                </Button>
              </CardContent>
            </Card>

            {/* Authentication Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plug className="h-5 w-5 text-blue-500" />
                  Authentication
                </CardTitle>
                <CardDescription>
                  Connect to KIOT using OAuth 2.0
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-gray-50 border">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium">Connection Status</span>
                    <Badge variant={authStatus?.authenticated ? "default" : "secondary"}>
                      {authStatus?.authenticated ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  {authStatus?.expires_at && (
                    <p className="text-sm text-gray-500">
                      Token expires: {new Date(authStatus.expires_at).toLocaleString()}
                    </p>
                  )}
                  {authStatus?.needs_refresh && (
                    <p className="text-sm text-amber-600 mt-2">⚠️ Token expired. Please refresh.</p>
                  )}
                </div>

                <div className="space-y-3">
                  {!authStatus?.authenticated ? (
                    <Button 
                      onClick={handleAuthenticate}
                      disabled={!config?.configured}
                      className="w-full"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Authenticate with KIOT
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleRefreshToken}
                      variant="outline"
                      className="w-full"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Token
                    </Button>
                  )}
                </div>

                {!config?.configured && (
                  <p className="text-sm text-gray-500 text-center">
                    Save your credentials first to authenticate
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* API Information */}
          <Card>
            <CardHeader>
              <CardTitle>API Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-700">Base URL</p>
                  <p className="text-gray-500 font-mono text-xs mt-1">https://api.kiot.io/integrations/ctp/go</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-700">Auth URL</p>
                  <p className="text-gray-500 font-mono text-xs mt-1">https://api.kiot.io/authstart</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-700">Token URL</p>
                  <p className="text-gray-500 font-mono text-xs mt-1">https://api.kiot.io/oauth/token</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hotel Mappings Tab */}
        <TabsContent value="hotels" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Hotel Mappings</CardTitle>
                <CardDescription>Map hotels to KIOT properties for device control</CardDescription>
              </div>
              <Button onClick={() => setAddMappingOpen(true)} disabled={unmappedHotels.length === 0}>
                <Plus className="h-4 w-4 mr-2" />
                Add Hotel
              </Button>
            </CardHeader>
            <CardContent>
              {hotelMappings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No hotels mapped yet</p>
                  <p className="text-sm">Add a hotel to start syncing devices</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Hotel</TableHead>
                      <TableHead>KIOT Property ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hotelMappings.map(mapping => (
                      <TableRow key={mapping.hotel_id}>
                        <TableCell className="font-medium">{mapping.hotel_name}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {mapping.kiot_property_id || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={mapping.is_enabled ? "default" : "secondary"}>
                            {mapping.is_enabled ? "Enabled" : "Disabled"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {new Date(mapping.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteMapping(mapping.hotel_id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Devices</CardTitle>
                <CardDescription>Manage IoT devices for hotels</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setAddDeviceOpen(true)}
                  variant="outline"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Device
                </Button>
                <Button 
                  onClick={() => setSyncDevicesOpen(true)} 
                  disabled={!authStatus?.authenticated || hotelMappings.length === 0}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync from KIOT
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {devices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Cpu className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No devices synced yet</p>
                  <p className="text-sm">Add devices manually or sync from KIOT</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {devices.map(device => {
                    const IconComponent = APPLIANCE_ICONS[device.ir_appliance_type] || APPLIANCE_ICONS[device.type] || Cpu;
                    const hotelName = hotels.find(h => h.id === device.hotel_id)?.name || 'Unknown Hotel';
                    return (
                      <Card key={device.id} className="border hover:shadow-md transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-blue-50">
                              <IconComponent className="h-6 w-6 text-blue-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <h4 className="font-medium truncate">{device.name}</h4>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDeleteDevice(device.id)}
                                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              <p className="text-sm text-gray-500">{device.room_hint || 'No room assigned'}</p>
                              <p className="text-xs text-gray-400 mt-1">{hotelName}</p>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <Badge variant="outline" className="text-xs">
                                  {entityTypes.find(t => t.code === device.type)?.name || 'Unknown'}
                                </Badge>
                                {device.ir_appliance_type && (
                                  <Badge variant="secondary" className="text-xs">
                                    {irTypes.find(t => t.code === device.ir_appliance_type)?.name || 'IR'}
                                  </Badge>
                                )}
                                {device.is_manual && (
                                  <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                                    Manual
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Supported Device Types */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Entity Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {entityTypes.map(type => (
                    <div key={type.code} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                      <Badge variant="outline">{type.code}</Badge>
                      <span>{type.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">IR Appliance Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {irTypes.map(type => {
                    const IconComponent = APPLIANCE_ICONS[type.code] || Cpu;
                    return (
                      <div key={type.code} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        <IconComponent className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">{type.name}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Hotel Mapping Dialog */}
      <Dialog open={addMappingOpen} onOpenChange={setAddMappingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Hotel Mapping</DialogTitle>
            <DialogDescription>
              Map a hotel to KIOT for device integration
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Hotel</Label>
              <Select value={selectedHotel} onValueChange={setSelectedHotel}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a hotel" />
                </SelectTrigger>
                <SelectContent>
                  {unmappedHotels.map(hotel => (
                    <SelectItem key={hotel.id} value={hotel.id}>{hotel.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMappingOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMapping}>Add Mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Devices Dialog */}
      <Dialog open={syncDevicesOpen} onOpenChange={setSyncDevicesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Sync Devices from KIOT</DialogTitle>
            <DialogDescription>
              Choose which device types to sync
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Hotel</Label>
              <Select value={selectedHotel} onValueChange={setSelectedHotel}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a hotel" />
                </SelectTrigger>
                <SelectContent>
                  {hotelMappings.map(mapping => (
                    <SelectItem key={mapping.hotel_id} value={mapping.hotel_id}>
                      {mapping.hotel_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Entity Types</Label>
              <div className="space-y-2">
                {entityTypes.map(type => (
                  <div key={type.code} className="flex items-center space-x-2">
                    <Checkbox
                      id={`entity-${type.code}`}
                      checked={syncEntityTypes.includes(type.code)}
                      onCheckedChange={() => toggleEntityType(type.code)}
                    />
                    <label htmlFor={`entity-${type.code}`} className="text-sm cursor-pointer">
                      {type.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {syncEntityTypes.includes(1) && (
              <div className="space-y-2">
                <Label>IR Appliance Filters (Optional)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {irTypes.map(type => (
                    <div key={type.code} className="flex items-center space-x-2">
                      <Checkbox
                        id={`ir-${type.code}`}
                        checked={syncIrFilters.includes(type.code)}
                        onCheckedChange={() => toggleIrFilter(type.code)}
                      />
                      <label htmlFor={`ir-${type.code}`} className="text-sm cursor-pointer">
                        {type.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncDevicesOpen(false)}>Cancel</Button>
            <Button onClick={handleSyncDevices} disabled={syncing || !selectedHotel}>
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Sync Devices
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Device Dialog */}
      <Dialog open={addDeviceOpen} onOpenChange={setAddDeviceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Device</DialogTitle>
            <DialogDescription>
              Manually add an IoT device to a hotel
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Device Name *</Label>
              <Input
                value={newDevice.name}
                onChange={(e) => setNewDevice(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Room 101 AC"
              />
            </div>

            <div className="space-y-2">
              <Label>Device Type *</Label>
              <Select 
                value={newDevice.device_type} 
                onValueChange={(v) => setNewDevice(prev => ({ ...prev, device_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select device type" />
                </SelectTrigger>
                <SelectContent>
                  {entityTypes.map(type => (
                    <SelectItem key={type.code} value={type.code.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newDevice.device_type === '1' && (
              <div className="space-y-2">
                <Label>IR Appliance Type</Label>
                <Select 
                  value={newDevice.ir_appliance_type} 
                  onValueChange={(v) => setNewDevice(prev => ({ ...prev, ir_appliance_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select IR appliance type" />
                  </SelectTrigger>
                  <SelectContent>
                    {irTypes.map(type => (
                      <SelectItem key={type.code} value={type.code.toString()}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Hotel *</Label>
              <Select 
                value={newDevice.hotel_id} 
                onValueChange={(v) => setNewDevice(prev => ({ ...prev, hotel_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select hotel" />
                </SelectTrigger>
                <SelectContent>
                  {hotels.map(hotel => (
                    <SelectItem key={hotel.id} value={hotel.id}>
                      {hotel.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Room / Location (Optional)</Label>
              <Input
                value={newDevice.room_hint}
                onChange={(e) => setNewDevice(prev => ({ ...prev, room_hint: e.target.value }))}
                placeholder="e.g., Room 101, Lobby, Conference Room A"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDeviceOpen(false)}>Cancel</Button>
            <Button onClick={handleAddDevice} disabled={addingDevice}>
              {addingDevice ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Device
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
